import {
  initializePaddle,
  Paddle,
  CheckoutEventNames,
} from "@paddle/paddle-js";

/**
 * Thin wrapper around Paddle.js for the instructor subscription overlay checkout.
 *
 * The client-side token and environment come from VITE env vars (the token is a
 * public, client-safe Paddle token — not a secret):
 *   VITE_PADDLE_CLIENT_TOKEN   — your Paddle "client-side token" (test_… in sandbox)
 *   VITE_PADDLE_ENVIRONMENT    — "sandbox" (default) or "production"
 *
 * Paddle is initialised once and the instance reused. When the token is absent the
 * helper resolves to undefined so the UI can show a clear "not configured" message
 * instead of crashing.
 */
let initPromise: Promise<Paddle | undefined> | null = null;

export const isPaddleConfigured = (): boolean =>
  Boolean(import.meta.env.VITE_PADDLE_CLIENT_TOKEN);

export const initPaddle = (
  onCheckoutCompleted: (transactionId: string) => void
): Promise<Paddle | undefined> => {
  if (initPromise) {
    return initPromise;
  }

  const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
  if (!token) {
    initPromise = Promise.resolve(undefined);
    return initPromise;
  }

  const environment =
    import.meta.env.VITE_PADDLE_ENVIRONMENT === "production"
      ? "production"
      : "sandbox";

  initPromise = initializePaddle({
    environment,
    token,
    eventCallback: (event) => {
      if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
        const transactionId = event.data?.transaction_id;
        if (transactionId) {
          onCheckoutCompleted(transactionId);
        }
      }
    },
  }).catch(() => undefined); // bad/placeholder token: treat as not configured

  return initPromise;
};

/** Thrown when checkout is attempted without a configured Paddle client token. */
export const PADDLE_NOT_CONFIGURED = "PADDLE_NOT_CONFIGURED";

export interface PaddleCheckout {
  priceId: string;
  customerId: string;
  instructorId: string;
}

/**
 * Opens the Paddle.js checkout overlay for the resolved price. Paddle creates the
 * transaction inline, so no account-level default payment link is needed. The
 * instructorId rides as custom data so the activation webhook maps the subscription
 * back onto the account.
 */
export const openPaddleCheckout = async (
  checkout: PaddleCheckout
): Promise<void> => {
  const paddle = await initPromise;
  if (!paddle) {
    throw new Error(PADDLE_NOT_CONFIGURED);
  }
  // Open with the price only and let the overlay collect the email. Passing a
  // server-created customer id here makes Paddle.js fail with a generic error
  // (a client-side token can't open checkout for an arbitrary existing customer).
  // The activation webhook maps the subscription via customData.instructorId.
  paddle.Checkout.open({
    items: [{ priceId: checkout.priceId, quantity: 1 }],
    customData: { instructorId: checkout.instructorId },
  });
};
